import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import SiteFooter from "@/components/SiteFooter";
import { TryDemo } from "@/components/TryDemo";
import SocialProofStats from "@/components/SocialProofStats";
import JoinDiscord from "@/components/JoinDiscord";
import { Gift, Flame, Trophy } from "lucide-react";

const BENEFITS = [
  [Gift, "Finish #1 on the monthly leaderboard to win a free OSRS Bond — never pay to win"],
  [Flame, "Auto-track completions & build daily streaks"],
  [Trophy, "Climb the leaderboards & challenge friends"],
];

export default function Auth() {
  const { login, register, googleLogin, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // {rsn, gm} demoed board to auto-track on signup
  const [source, setSource] = useState("");
  const [guestSave, setGuestSave] = useState(null); // returning-guest bank nudge
  const [refCode] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("ref") || ""; } catch { return ""; }
  });

  // Bank a guest board (create profile + claim verified guest tasks) after any signup/login.
  const bankGuestAndTrack = async (rsn, gm) => {
    try {
      const { data: prof } = await api.post("/profiles", { username: rsn, gamemode: gm || "main" });
      try {
        const g = JSON.parse(localStorage.getItem("ts_guest") || "null");
        if (g?.rsn && g.rsn.toLowerCase() === rsn.toLowerCase() && (g.done || []).length) {
          const { data: bank } = await api.post(`/profiles/${prof.id}/claim-guest`, { tasks: g.done });
          if (bank.banked > 0) toast.success(`Banked ${bank.banked} guest task${bank.banked > 1 ? "s" : ""} · +${bank.points} points!`);
          localStorage.removeItem("ts_guest");
        }
      } catch (_) {}
      toast.success(`Now tracking ${rsn} — your board is saved!`);
    } catch (_) {
      toast.warning(`Account created! We couldn't auto-add ${rsn} — add it from your dashboard.`);
    }
  };

  // Returning guest with unsaved points → show a "claim them" nudge.
  React.useEffect(() => {
    try {
      const g = JSON.parse(localStorage.getItem("ts_guest") || "null");
      if (g?.rsn && (g.done || []).length && (g.points || 0) > 0) {
        setGuestSave({ rsn: g.rsn, points: g.points, gm: g.gamemode });
      }
    } catch (_) {}
  }, []);

  // Emergent Google OAuth callback: exchange session_id (URL fragment) for our JWT.
  React.useEffect(() => {
    const m = (window.location.hash || "").match(/session_id=([^&]+)/);
    if (!m) return;
    const sid = decodeURIComponent(m[1]);
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    setBusy(true);
    googleLogin(sid)
      .then(async () => {
        toast.success("Signed in with Google!");
        try {
          const g = JSON.parse(localStorage.getItem("ts_guest") || "null");
          if (g?.rsn && (g.done || []).length) await bankGuestAndTrack(g.rsn, g.gamemode);
        } catch (_) {}
        navigate("/");
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Google sign-in failed. Please try again."))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const googleRedirect = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  React.useEffect(() => { if (refCode) setMode("register"); }, [refCode]);

  // Arriving from guest mode: prefill the RSN and switch to register so we can bank their board.
  React.useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const claim = sp.get("claim");
      if (claim) {
        setMode("register");
        setName(claim);
        setPending({ rsn: claim, gm: sp.get("gm") || "main" });
      }
    } catch (_) {}
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back, adventurer!");
      } else {
        await register(name, email, password, refCode || undefined);
        try { localStorage.setItem("ts_new_signup", "1"); } catch (_) {}
        if (source) { try { await api.post("/me/attribution", { source }); } catch (_) {} }
        if (pending?.rsn) {
          await bankGuestAndTrack(pending.rsn, pending.gm);
        } else {
          toast.success("Welcome, adventurer!");
        }
      }
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stone-bg App flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-4xl">
        <div className="flex flex-col items-center mb-5 ts-rise">
          <Logo variant="hero" />
          <div data-testid="bond-guarantee-badge" className="mt-3 inline-flex items-center gap-2">
            <img src="/bond.png" alt="OSRS Bond" width={16} height={16} className="[image-rendering:pixelated]" />
            <span className="osrs-shadow text-[#7CFC7C] text-xs">Finish #1 on the monthly leaderboard to win a free OSRS Bond — <span className="text-[#00FF00] font-bold">fully guaranteed by TaskScape.quest</span>. Never pay to win.</span>
          </div>
          <div className="w-full max-w-md"><SocialProofStats /></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start ts-stagger">
          {guestSave && (
            <div data-testid="guest-bank-nudge" className="md:col-span-2 osrs-panel p-3 flex flex-col sm:flex-row sm:items-center gap-2 border-[#3f6a2a]">
              <p className="osrs-shadow text-[#E6A519] text-sm flex-1">
                👋 Welcome back! You have <span className="osrs-num text-[#7CFC7C]">{guestSave.points} points</span> waiting on
                <span className="osrs-num text-[#FFCF3F]"> {guestSave.rsn}</span> — create a free account to bank them.
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <a data-testid="guest-bank-resume" href={`/guest/${encodeURIComponent(guestSave.rsn)}?gm=${guestSave.gm || "main"}`}
                  className="osrs-btn px-3 py-1.5 text-xs">Resume board</a>
                <button data-testid="guest-bank-claim" onClick={() => {
                  setMode("register"); setError(""); setName(guestSave.rsn); setPending({ rsn: guestSave.rsn, gm: guestSave.gm });
                }} className="osrs-btn osrs-btn-green px-3 py-1.5 text-xs">Save &amp; claim →</button>
              </div>
            </div>
          )}
          <TryDemo onRegister={(rsn, gm) => { setMode("register"); setError(""); if (rsn) { setName(rsn); setPending({ rsn, gm }); } }} />

          <div className="osrs-panel p-6 min-w-0 w-full">
          <div className="mb-5">
            <p className="osrs-title text-base mb-2.5">Why create an account?</p>
            <div className="space-y-1.5" data-testid="auth-benefits">
              {BENEFITS.map(([Icon, text]) => (
                <div key={text} className="flex items-center gap-2">
                  <Icon size={15} className="text-[#7CFC7C] shrink-0" />
                  <span className="osrs-shadow text-[#E6A519] text-xs leading-snug">{text}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-6">
            <button
              data-testid="tab-login"
              onClick={() => { setMode("login"); setError(""); }}
              className={`osrs-btn py-2 text-sm ${mode === "login" ? "" : "opacity-60"}`}
            >
              Sign In
            </button>
            <button
              data-testid="tab-register"
              onClick={() => { setMode("register"); setError(""); }}
              className={`osrs-btn py-2 text-sm ${mode === "register" ? "" : "opacity-60"}`}
            >
              Register
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <button
              type="button"
              data-testid="google-login-btn"
              onClick={googleRedirect}
              disabled={busy}
              className="osrs-btn w-full py-2.5 flex items-center justify-center gap-2 text-sm bg-white text-[#1a1a1a] hover:brightness-95"
            >
              <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2C41 36.9 44 31 44 24c0-1.3-.1-2.3-.4-3.5z"/>
              </svg>
              Continue with Google
            </button>
            <div className="flex items-center gap-3 py-1">
              <span className="flex-1 h-px bg-[#4b3a1e]" />
              <span className="osrs-shadow text-[#8a7a54] text-xs uppercase">or</span>
              <span className="flex-1 h-px bg-[#4b3a1e]" />
            </div>
            {mode === "register" && refCode && (
              <p data-testid="referral-hint" className="osrs-shadow text-[#7CFC7C] text-xs bg-[#1c2a16] border border-[#3f6a2a] p-2">
                🎁 You were invited! Sign up and <span className="text-[#FFCF3F]">you both get +1 free skip</span>.
              </p>
            )}
            {mode === "register" && pending?.rsn && (
              <p data-testid="claim-hint" className="osrs-shadow text-[#7CFC7C] text-xs bg-[#1c2a16] border border-[#3f6a2a] p-2">
                We'll start tracking <span className="text-[#FFCF3F] osrs-num">{pending.rsn}</span> the moment you sign up — your board is saved instantly.
              </p>
            )}
            {mode === "register" && (
              <div>
                <label className="osrs-shadow text-sm block mb-1">Display Name</label>
                <input
                  data-testid="input-name"
                  className="osrs-input w-full px-3 py-2"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Zezima"
                  required
                />
              </div>
            )}
            <div>
              <label className="osrs-shadow text-sm block mb-1">Email</label>
              <input
                data-testid="input-email"
                type="email"
                className="osrs-input w-full px-3 py-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@gielinor.com"
                required
              />
            </div>
            <div>
              <label className="osrs-shadow text-sm block mb-1">Password</label>
              <input
                data-testid="input-password"
                type="password"
                className="osrs-input w-full px-3 py-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            {mode === "register" && (
              <div>
                <label className="osrs-shadow text-sm block mb-1">How did you hear about us? <span className="text-[#8a7a54] text-xs">(optional)</span></label>
                <select
                  data-testid="input-source"
                  className="osrs-input w-full px-3 py-2"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                >
                  <option value="">Prefer not to say</option>
                  <option value="Reddit">Reddit</option>
                  <option value="Discord">Discord / clan</option>
                  <option value="YouTube">YouTube</option>
                  <option value="Twitch">Twitch / stream</option>
                  <option value="Friend">A friend</option>
                  <option value="Search">Google / search</option>
                  <option value="Twitter">Twitter / X</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            )}

            {error && (
              <p data-testid="auth-error" className="text-[#FF6666] osrs-shadow text-sm bg-black/40 p-2 border border-[#5a2020]">
                {error}
              </p>
            )}

            <button data-testid="auth-submit" type="submit" disabled={busy} className="osrs-btn w-full py-3">
              {busy ? "..." : mode === "login" ? "Sign In" : pending?.rsn ? "Claim my board" : "Create Account"}
            </button>
          </form>
        </div>
        </div>
      </div>
      <div className="w-full max-w-4xl flex flex-col items-center gap-3 mt-6">
        <JoinDiscord label="Join our Discord" size={18} className="px-5 py-2.5 text-sm rounded-sm" />
        <SiteFooter />
      </div>
    </div>
  );
}
